import React from 'react';
import miniHandlebars from '@tmap/mmm-core/miniHandlebars';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';

import { PortableText } from '@portabletext/react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { buildMarksTree } from '@portabletext/toolkit';

import GridBlock from './grid';
import Hero from './hero';
import IFrame from './iframe';
import CalloutBox from './calloutBox';
import CaptionedImage from './captionedImage';
import SmartLink from './smart-link';
import Video from './video';
import HowItWorksCard from '../offers/howItWorksCard';
import RelatedOffers from './relatedOffers';
import RelatedCommunities from './relatedCommunities';
import BannerBlock from './banner';

const BlockContentGrid = styled(Grid, {
  shouldForwardProp: (p) => !['isCentered', 'nested'].includes(p),
})(({ theme, isCentered, nested }) => ({
  ...(isCentered ? { textAlign: 'center' } : {}),
  ...(nested ? {} : {
    [theme.breakpoints.only('xs')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  }),
}));

function GridWrapper(props) {
  const { isCentered, nested, children } = props;
  const content = (
    <BlockContentGrid xs={12} item isCentered={isCentered} nested={nested}>
      {children}
    </BlockContentGrid>
  );
  return nested ? content : (
    <>
      <Grid xs={false} sm={2} item />
      {content}
      <Grid xs={false} sm={2} item />
    </>
  );
}

const BlockQuote = styled(Typography)(({ theme }) => ({
  borderLeft: `2px solid ${theme.palette.secondary.main}`,
  padding: '2% 4%',
  lineHeight: '167%',
}));

const components = (parentProps, RecursiveComponent) => ({
  types: {
    block(props) {
      // TODO: Remove me and buildMarksTree import if they add children back to props again.
      if (!props.children) {
        // eslint-disable-next-line no-param-reassign
        props = {
          ...props,
          children: buildMarksTree(props.value).map((child, i) => props.renderNode({
            node: child,
            isInline: true,
            index: i,
            renderNode: props.renderNode,
          })),
        };
      }

      switch (props.value.style) {
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
        case 'subtitle1':
        case 'subtitle2':
        case 'body2':
        case 'body1':
        case 'caption':
          return (
            <GridWrapper nested={parentProps.nested} gutterBottom>
              <Typography variant={props.value.style} gutterBottom>
                {
                  typeof props.children === 'string'
                    ? miniHandlebars(props.children, parentProps.handlebarsData)
                    : props.children
                }
              </Typography>
            </GridWrapper>
          );
        case 'h1-med':
          return (
            <GridWrapper nested={parentProps.nested} gutterBottom>
              <Typography variant='h2' component='h1' gutterBottom>
                {props.children}
              </Typography>
            </GridWrapper>
          );
        case 'h1-small':
          return (
            <GridWrapper nested={parentProps.nested} gutterBottom>
              <Typography variant='h3' component='h1' gutterBottom>
                {props.children}
              </Typography>
            </GridWrapper>
          );
        case 'blockquote':
          return (
            <GridWrapper nested={parentProps.nested}>
              <BlockQuote variant='h4'>
                {props.children}
              </BlockQuote>
            </GridWrapper>
          );
        default:
          return props.children.length === 1 && props.children[0] === '' ? (
            <GridWrapper nested={parentProps.nested}><br /></GridWrapper>
          ) : (
            <GridWrapper nested={parentProps.nested}>
              <Typography variant='body1' gutterBottom>
                {props.children}
              </Typography>
            </GridWrapper>
          );
      }
    },
    button(props) {
      const {
        isCentered = false,
        fullWidth = false,
        url = '',
      } = props.value;
      return (
        <GridWrapper nested={parentProps.nested} isCentered={isCentered}>
          <SmartLink to={url}>
            <Button
              variant={props.value.buttonType || 'contained'}
              color={props.value.color || 'default'}
              size={props.value.size || 'medium'}
              fullWidth={fullWidth}
            >
              {props.value.text}
            </Button>
          </SmartLink>
        </GridWrapper>
      );
    },
    hero(props) {
      return (
        <Grid item xs={12}>
          <Hero {...props.value} />
        </Grid>
      );
    },
    grid(props) {
      return (
        <GridWrapper nested={parentProps.nested}>
          <GridBlock {...props.value} />
        </GridWrapper>
      );
    },
    iframe(props) {
      return (
        <GridWrapper nested={parentProps.nested}>
          <IFrame {...props.value} />
        </GridWrapper>
      );
    },
    captionedImage(props) {
      return (
        <GridWrapper nested={parentProps.nested}>
          <CaptionedImage {...props.value} />
        </GridWrapper>
      );
    },
    calloutBox(props) {
      return (
        <GridWrapper nested={parentProps.nested}>
          <CalloutBox RecursiveComponent={RecursiveComponent} {...props.value} />
        </GridWrapper>
      );
    },
    videoBlock(props) {
      return (
        <GridWrapper nested={parentProps.nested}>
          <Video {...props.value} />
        </GridWrapper>
      );
    },
    howItWorks() {
      return (
        <GridWrapper nested={parentProps.nested}>
          <Box sx={{ mt: 5, mb: 5 }}>
            <HowItWorksCard />
          </Box>
        </GridWrapper>
      );
    },
    relatedOffers(props) {
      return (
        <GridWrapper nested={parentProps.nested}>
          <Box sx={{ mt: 5, mb: 5 }}>
            <RelatedOffers
              pageId={parentProps.pageId}
              {...props.value}
            />
          </Box>
        </GridWrapper>
      );
    },
    relatedCommunities(props) {
      return (
        <GridWrapper nested={parentProps.nested}>
          <Box sx={{ mt: 5, mb: 5 }}>
            <RelatedCommunities
              pageId={parentProps.pageId}
              {...props.value}
            />
          </Box>
        </GridWrapper>
      );
    },
    bannerBlock(props) {
      return (
        <GridWrapper nested={parentProps.nested}>
          <Box sx={{ mt: 5, mb: 5 }}>
            <BannerBlock
              RecursiveComponent={RecursiveComponent}
              pageId={parentProps.pageId}
              {...props.value}
            />
          </Box>
        </GridWrapper>
      );
    },
  },
  container: (props) => (
    <Grid container>{props.children}</Grid>
  ),
  marks: {
    color: (props) => (
      <span style={{ color: props.value.hex }}>{props.children}</span>
    ),
    link: (props) => <SmartLink to={props.value.href} variant={parentProps.linkVariant || 'primary'}>{props.children}</SmartLink>,
  },
  listItem: (props) => (
    <li><Typography component='span' variant='body1'>{props.children}</Typography></li>
  ),
});

function BlockContent({ value, nested = false, ...otherProps }) {
  return (
    <PortableText
      value={value}
      components={components({ nested, ...otherProps }, BlockContent)}
      nested={nested}
      renderContainerOnSingleChild
    />
  );
}

export default BlockContent;
